// src/components/JobMapComponent.js
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import {
  initializeMap, addMarker, setMapView, removeMarker, fixDefaultIconPaths,
  updateMarkerPosition
} from '../utils/mapUtils'; // Adjust path as needed

// Helper function to geocode address using Nominatim
const geocodeAddressNominatim = async (addressString) => {
  // console.log(`[geocodeAddressNominatim] Attempting to geocode: "${addressString}"`);
  const encodedAddress = encodeURIComponent(addressString);
  // IMPORTANT: Replace 'YourAppName/1.0 (your-contact-email@example.com)' with actual app info for Nominatim policy
  const userAgent = 'WhiteKnightPortal/1.0 (support@whiteknightroads.com)'; 
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`;

  try {
    const response = await fetch(url, {
      headers: {
        'User-Agent': userAgent
      }
    });
    if (!response.ok) {
      throw new Error(`Nominatim API request failed with status: ${response.status}`);
    }
    const data = await response.json();

    if (data && data.length > 0) {
      const { lat, lon } = data[0];
      const latitude = parseFloat(lat);
      const longitude = parseFloat(lon);
      
      if (!isNaN(latitude) && !isNaN(longitude)) {
        // console.log(`[geocodeAddressNominatim] Geocoding successful: lat=${latitude}, lng=${longitude}`);
        return { lat: latitude, lng: longitude }; // Return lng for consistency with Leaflet
      } else {
         throw new Error('Invalid coordinates received from Nominatim.');
      }
    } else {
      console.warn(`[geocodeAddressNominatim] No results found for address: "${addressString}"`);
      return null;
    }
  } catch (error) {
    console.error(`[geocodeAddressNominatim] Error:`, error);
    return null; // Indicate failure
  }
};

// Browser-persistent geocode cache: an address is looked up at most once per
// browser, ever — reopening a dialog/page reuses the stored answer for free.
const GEOCODE_CACHE_KEY = 'wk_geocode_cache_v1';
let geocodeCache = (() => {
  try { return JSON.parse(localStorage.getItem(GEOCODE_CACHE_KEY)) || {}; } catch { return {}; }
})();
const geocodeCacheGet = (key) => geocodeCache[key] || null;
const geocodeCacheSet = (key, coords) => {
  geocodeCache[key] = coords;
  const keys = Object.keys(geocodeCache);
  if (keys.length > 300) {
    geocodeCache = Object.fromEntries(keys.slice(-200).map((k) => [k, geocodeCache[k]]));
  }
  try { localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(geocodeCache)); } catch { /* storage full/blocked — cache stays in-memory */ }
};

// Reusable Map component with geocoding fallback
const JobMapComponentInner = ({ locationData }) => { // Export the component
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [geocodedCoords, setGeocodedCoords] = useState(null); // Store {lat, lng} from DB or Geocoding
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodingError, setGeocodingError] = useState(null);
  // Lazy locate: when an address has no known coordinates, we wait for the
  // user to click the map instead of geocoding automatically
  const [needsLocate, setNeedsLocate] = useState(false);
  const pendingAddressRef = useRef(null); // address string awaiting a locate click

  // --- Effects ---

  // Track component mount
  useEffect(() => {
    setHasMounted(true);
  }, []);

  // Measure container dimensions before paint (for debugging)
  useLayoutEffect(() => {
    if (mapContainerRef.current) {
      // console.log('[JobMapComponent] useLayoutEffect - measuring container before paint');
      const containerHeight = mapContainerRef.current.clientHeight;
      const containerWidth = mapContainerRef.current.clientWidth;
      // console.log(`[JobMapComponent] Container dimensions (before paint): ${containerWidth}x${containerHeight}`);
    }
  }, []);

  // Initialize the map instance
  useEffect(() => {
    let mapInstance = null;
    if (hasMounted && mapContainerRef.current && !mapRef.current) {
      // console.log('[JobMapComponent Init Effect] Initializing map...');
      fixDefaultIconPaths();
      // Initialize with default center/zoom, actual location set by update effect
      mapInstance = initializeMap(mapContainerRef.current, {
        center: [39.8283, -98.5795], // Default US center
        zoom: 4,
      });
      mapRef.current = mapInstance;
      setMapInitialized(true);

      // Force resize after initialization
      setTimeout(() => {
         if (mapRef.current) {
            // console.log('[JobMapComponent Init Effect] Forcing map resize');
            mapRef.current.invalidateSize(true);
         }
      }, 100); // Short delay
    }

    // Cleanup
    return () => {
      if (mapRef.current) { // Use mapRef.current for cleanup check
        mapRef.current.remove();
        mapRef.current = null;
        setMapInitialized(false);
        markerRef.current = null; // Clear marker ref on cleanup
      }
    };
  // Depend only on mount status
  }, [hasMounted]); 

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (mapRef.current) {
        mapRef.current.invalidateSize(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Resolve coordinates WITHOUT calling the geocoder: stored DB coords first,
  // then the browser cache. An unknown address just arms "click to locate" —
  // the only thing that ever triggers a live geocode is a click on the map.
  useEffect(() => {
    // Use locationData prop instead of job
    if (!locationData) {
      setGeocodedCoords(null);
      setGeocodingError(null);
      setIsGeocoding(false);
      setNeedsLocate(false);
      pendingAddressRef.current = null;
      return;
    }

    // Reset state for new check
    setGeocodedCoords(null);
    setGeocodingError(null);
    setIsGeocoding(false);
    setNeedsLocate(false);
    pendingAddressRef.current = null;

    // 1. Stored database coordinates win (saved jobs/facilities have these)
    const nestedLocation = locationData?.location;
    const coordinates = nestedLocation?.coordinates;
    if (Array.isArray(coordinates) && coordinates.length === 2 &&
        typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number' &&
        (coordinates[0] !== 0 || coordinates[1] !== 0)) { // Check valid numbers AND not [0, 0]
      setGeocodedCoords({ lat: coordinates[1], lng: coordinates[0] });
      return;
    }
    // 1b. Coordinates typed in the job dialog (lat/lng fields) before the job is saved
    const typedLat = Number(locationData?.lat);
    const typedLng = Number(locationData?.lng);
    if (locationData?.lat !== '' && locationData?.lat != null && locationData?.lng !== '' && locationData?.lng != null &&
        Number.isFinite(typedLat) && Number.isFinite(typedLng) && Math.abs(typedLat) <= 90 && Math.abs(typedLng) <= 180 &&
        (typedLat !== 0 || typedLng !== 0)) {
      setGeocodedCoords({ lat: typedLat, lng: typedLng });
      return;
    }

    // Construct address string from locationData, excluding suite/apt info
    const streetBase = locationData?.street?.split(/ (?:suite|ste|apt|#|unit) /i)[0];
    const addressParts = [
      streetBase,
      locationData?.city,
      locationData?.state,
      locationData?.zip,
      (locationData?.country && locationData.country.toUpperCase() !== 'USA') ? locationData.country : null
    ].filter(part => part).join(', ');

    // Only lookable addresses arm the locate flow (5-digit zip, or city+state)
    const hasZip = /\b\d{5}\b/.test(locationData?.zip || '');
    const hasCityState = !!(locationData?.city && locationData?.state && String(locationData.state).length >= 2);
    if (!addressParts || !(hasZip || hasCityState)) {
      return; // blank/partial address → quiet default map
    }

    // 2. Browser cache: previously located addresses cost nothing
    const cached = geocodeCacheGet(addressParts);
    if (cached) {
      setGeocodedCoords(cached);
      return;
    }

    // 3. Unknown address: wait for a click on the map
    pendingAddressRef.current = addressParts;
    setNeedsLocate(true);
  }, [locationData]); // Rerun when locationData changes

  // Click-to-locate: the single trigger for a live geocode
  useEffect(() => {
    if (!mapInitialized || !mapRef.current) return;
    const map = mapRef.current;

    const handleLocateClick = () => {
      const address = pendingAddressRef.current;
      if (!address) return; // nothing armed — coords already known
      pendingAddressRef.current = null;
      setNeedsLocate(false);
      setIsGeocoding(true);
      geocodeAddressNominatim(address)
        .then((coords) => {
          if (coords) {
            geocodeCacheSet(address, coords);
            setGeocodedCoords(coords);
            setGeocodingError(null);
          } else {
            setGeocodingError('Address not found on the map.');
            setGeocodedCoords(null);
          }
        })
        .catch(() => {
          setGeocodingError('An error occurred locating the address.');
          setGeocodedCoords(null);
        })
        .finally(() => setIsGeocoding(false));
    };

    map.on('click', handleLocateClick);
    return () => { map.off('click', handleLocateClick); };
  }, [mapInitialized]);

  // Update Map View/Marker based on final coordinates (DB or Geocoded)
  useEffect(() => {
    // Only proceed if map is initialized and we are not currently geocoding
    if (mapInitialized && mapRef.current && !isGeocoding) {
      const currentMap = mapRef.current;
      const serviceLocationData = locationData; // Use locationData prop
      let finalCoords = null;
      let hasValidCoords = false;

      // Check the geocodedCoords state (which holds either valid DB or geocoded results)
      if (geocodedCoords && typeof geocodedCoords.lat === 'number' && typeof geocodedCoords.lng === 'number' &&
          (geocodedCoords.lat !== 0 || geocodedCoords.lng !== 0)) {
        finalCoords = [geocodedCoords.lat, geocodedCoords.lng];
        hasValidCoords = true;
        // console.log(`[JobMapComponent Map Update Effect] Using Valid Coords: ${finalCoords}`);
      } else {
         // console.log(`[JobMapComponent Map Update Effect] No valid coords available (DB or Geocoded).`);
      }

      // Update map based on whether we have valid coordinates
      if (hasValidCoords) {
        const popupContent = `Location: ${serviceLocationData?.street || 'Unknown Street'}, ${serviceLocationData?.city || ''} ${serviceLocationData?.state || ''}`; // Generic popup
        if (markerRef.current) {
          // console.log('[JobMapComponent Map Update Effect] Updating existing marker position');
          updateMarkerPosition(markerRef.current, finalCoords);
          if (markerRef.current.getPopup()) {
             markerRef.current.setPopupContent(popupContent);
          } else {
             markerRef.current.bindPopup(popupContent);
          }
        } else {
           // console.log('[JobMapComponent Map Update Effect] Adding new marker');
           markerRef.current = addMarker(currentMap, finalCoords, {}, popupContent);
        }
        // console.log(`[JobMapComponent Map Update Effect] Setting view to coordinates: [${finalCoords}]`);
        setMapView(currentMap, finalCoords, 14);
      } else {
        // No valid coordinates found (and not currently geocoding), default the view
        // console.log('[JobMapComponent Map Update Effect] No valid coords found, using default view');
        setMapView(currentMap, [39.8283, -98.5795], 4); // Default US center
        if (markerRef.current) {
          removeMarker(currentMap, markerRef.current);
          markerRef.current = null;
        }
      }
      // Force map resize after view/marker updates
      // console.log('[JobMapComponent Map Update Effect] Forcing map resize');
      currentMap.invalidateSize(true);

    } else if (isGeocoding) {
        // console.log('[JobMapComponent Map Update Effect] Waiting for geocoding to complete...');
    }
  // Depend on map init status, the geocoded coordinates result, geocoding status, and locationData for popup updates
  }, [mapInitialized, geocodedCoords, isGeocoding, locationData]); 

  // Render the map container div
  return (
    <div
      ref={mapContainerRef}
      style={{ height: '100%', width: '100%', position: 'relative' }} // Fill parent height, position relative for overlay
    >
      {/* Display loading overlay during geocoding */}
      {isGeocoding && (
        <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center',
          zIndex: 1000 // Ensure it's above map tiles
        }}>
          <CircularProgress />
          <Typography sx={{ mt: 1, fontWeight: 'medium' }}>Geocoding Address...</Typography>
        </Box>
      )}
      {/* Invite a locate click when the address has no known coordinates.
          pointerEvents none lets the click fall through to the map itself. */}
      {needsLocate && !isGeocoding && (
        <Box sx={{
          position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)',
          backgroundColor: 'rgba(25, 118, 210, 0.92)', color: '#fff',
          px: 2, py: 0.75, borderRadius: 1, zIndex: 1000,
          pointerEvents: 'none', whiteSpace: 'nowrap'
        }}>
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            📍 Click the map to locate this address
          </Typography>
        </Box>
      )}
      {/* Display error message if geocoding failed */}
      {geocodingError && !isGeocoding && (
         <Box sx={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000, p: 2, textAlign: 'center'
        }}>
          <Typography color="error" variant="body2">
            Could not determine location. {geocodingError}
          </Typography>
        </Box>
      )}
      {/* Map container div content is implicitly handled by Leaflet attaching to the ref */}
    </div>
  );
};

export const JobMapComponent = React.memo(JobMapComponentInner);

// Removed DetailsTabContent, JobImage, ImagePreviewSection, JobDetails components as they remain in JobDetails.js